import { useEffectEvent } from 'react-aria/private/utils/useEffectEvent';
import { useResizeObserver } from 'react-aria/private/utils/useResizeObserver';
import { RefObject, useEffect, useState } from 'react';
import { useMutationObserver } from './useMutationObserver';

export interface AnimatedSegmentState {
  canAnimate: boolean;
  position: {
    width: number;
    offsetLeft: number;
  };
}

export interface UseAnimatedSegmentProps {
  containerRef: RefObject<HTMLElement>;
  activeElementRef: RefObject<HTMLElement>;
  onComputeRequest: (element: HTMLElement) => {
    width: number;
    offsetLeft: number;
  };
}

const useAnimatedSegment = (
  { containerRef, activeElementRef, onComputeRequest }: UseAnimatedSegmentProps,
  deps?: React.DependencyList
): AnimatedSegmentState => {
  const [isMounted, setIsMounted] = useState(false);
  const [position, setPosition] = useState({
    width: 0,
    offsetLeft: 0,
  });

  const updateSegmentPosition = useEffectEvent(() => {
    if (!activeElementRef.current) {
      setPosition({ width: 0, offsetLeft: 0 });
      return;
    }

    const futurePosition = onComputeRequest(activeElementRef.current);
    setPosition(futurePosition);
  });

  useResizeObserver({
    ref: containerRef,
    onResize: updateSegmentPosition,
  });

  useResizeObserver({
    ref: activeElementRef,
    onResize: updateSegmentPosition,
  });

  useMutationObserver({
    ref: containerRef,
    onMutation: updateSegmentPosition,
  });

  useEffect(() => {
    // Used to avoid glider transition on first render
    if (!isMounted && position.width !== 0) {
      setTimeout(() => {
        setIsMounted(true);
      }, 10);
    }
  }, [isMounted, position.width]);

  useEffect(() => {
    updateSegmentPosition();
  }, [activeElementRef.current, ...(deps || [])]);

  return {
    canAnimate: isMounted,
    position,
  };
};

export default useAnimatedSegment;
